import type { User } from "@repo/exit-calc-interfaces";
import type { StoreDeps } from "../types";
import { createStore } from "../helpers/create-store";

type AuthStatus = "idle" | "loading" | "success";

interface AuthState {
  user: User | null;
  status: AuthStatus;
  isLogoutInProgress: boolean;
}

interface AuthActions {
  updateStatus: (status: AuthStatus) => void;
  updateUser: (user: User) => void;
  init: () => Promise<void>;
  logout: () => Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

export function createAuthStore(deps: StoreDeps) {
  return createStore<AuthStore>("AuthStore", (set, get) => ({
    user: null,
    status: "idle",
    isLogoutInProgress: false,

    updateStatus: (status) =>
      set((state) => {
        state.status = status;
      }),

    updateUser: (user) =>
      set((state) => {
        state.user = user;
      }),

    init: async () => {
      try {
        get().updateStatus("loading");
        const user = await deps.api.user.get();
        get().updateUser(user);
      } catch (error) {
        deps.logger.error(error);
      } finally {
        get().updateStatus("success");
      }
    },

    logout: async () => {
      set((state) => {
        state.isLogoutInProgress = true;
      });
      get().updateStatus("loading");
      try {
        await deps.api.user.logout();
        set((state) => {
          state.user = null;
        });
        get().updateStatus("success");
      } catch (error) {
        deps.logger.error(error);
      } finally {
        get().updateStatus("idle");
        set((state) => {
          state.isLogoutInProgress = false;
        });
      }
    },
  }));
}
